import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, PoundSterling, Receipt, Download } from 'lucide-react';

const STATUS = { paid: 'bg-green-100 text-green-700', initiated: 'bg-amber-100 text-amber-700', pending: 'bg-amber-100 text-amber-700', expired: 'bg-slate-100 text-slate-600', failed: 'bg-red-100 text-red-700', refunded: 'bg-purple-100 text-purple-700' };
const money = (n) => `£${Number(n || 0).toFixed(2)}`;

export default function AdminPaymentsPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('paid');
  useEffect(() => { api.get('/admin/reports/payments').then((r) => setData(r.data)).catch(() => setData({ items: [], total_revenue: 0, paid_count: 0 })); }, [api]);
  const rows = useMemo(() => (data?.items || []).filter((r) => (type === 'all' || r.type === type) && (status === 'all' || r.status === status)
    && `${r.item} ${r.customer} ${r.email}`.toLowerCase().includes(q.toLowerCase())), [data, q, type, status]);
  const exportCsv = () => {
    const csv = ['Date,Type,Item,Customer,Email,Amount,Status'].concat(rows.map((r) => [r.created_at?.slice(0, 10), r.type, r.item, r.customer, r.email, r.amount, r.status].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'payments.csv';
    a.click();
  };
  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="admin-payments-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Payments</h1>
            <p className="text-slate-500 mt-1 text-sm">Every card payment for courses, subscriptions, bundles and e-books.</p>
          </div>
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="payments-export"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="border-slate-200"><CardContent className="p-5 flex items-center gap-3"><div className="p-2 rounded-lg bg-green-50 text-green-600"><PoundSterling className="h-5 w-5" /></div><div><p className="text-xl font-bold text-green-600" data-testid="payments-total-revenue">{data ? money(data.total_revenue) : '—'}</p><p className="text-xs text-slate-500">Total collected</p></div></CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-5 flex items-center gap-3"><div className="p-2 rounded-lg bg-sky-50 text-[#20acf6]"><Receipt className="h-5 w-5" /></div><div><p className="text-xl font-bold text-[#20acf6]" data-testid="payments-paid-count">{data ? data.paid_count : '—'}</p><p className="text-xs text-slate-500">Successful payments</p></div></CardContent></Card>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item, customer or email…" className="pl-10" data-testid="payments-search" />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-40" data-testid="payments-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{['all', 'Course', 'Subscription', 'Bundle', 'E-book'].map((t) => <SelectItem key={t} value={t}>{t === 'all' ? 'All types' : t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{[['paid', 'Paid'], ['all', 'All statuses'], ['initiated', 'Started, not paid'], ['expired', 'Expired']].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {!data ? <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
              : rows.length === 0 ? <p className="text-center py-16 text-slate-500" data-testid="payments-empty">No payments match these filters yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200"><tr>{['Date', 'Type', 'Item', 'Customer', 'Amount', 'Status'].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-600">{h}</th>)}</tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={`${r.type}-${r.id}`} className="border-b border-slate-100" data-testid={`payment-row-${r.id}`}>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{r.type}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{r.item}</td>
                          <td className="px-4 py-3"><p className="text-slate-700">{r.customer}</p><p className="text-xs text-slate-400">{r.email}</p></td>
                          <td className="px-4 py-3 font-medium">{money(r.amount)}</td>
                          <td className="px-4 py-3"><Badge className={`${STATUS[r.status] || 'bg-slate-100 text-slate-600'} text-[10px] capitalize`}>{r.status === 'initiated' ? 'Not paid' : r.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
